import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import CelebrationOutlinedIcon from "@mui/icons-material/CelebrationOutlined";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import LensIcon from "@mui/icons-material/Lens";
import RectangleOutlinedIcon from "@mui/icons-material/RectangleOutlined";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import WaterDropOutlinedIcon from "@mui/icons-material/WaterDropOutlined";
import type { ReactNode } from "react";
import { PillToggle } from "@/components/campaignBuilder/design/PillToggle";
import { CONFETTI_SCHEMES, CONFETTI_SHAPES } from "@/constants/landing";
import type { LandingSettings } from "@/context/CampaignBuilderContext";
import type { ConfettiShape } from "@/interfaces/redemption";

interface ConfettiControlsProps {
  settings: LandingSettings;
  onChange: (v: Partial<LandingSettings>) => void;
  /** Fire a sample burst with the given scheme (null ⇒ default brand palette). */
  onPreview: (schemeIndex: number | null) => void;
}

const SHAPE_ICONS: Record<ConfettiShape, ReactNode> = {
  mixed: <AutoAwesomeOutlinedIcon sx={{ fontSize: 14 }} />,
  teardrop: <WaterDropOutlinedIcon sx={{ fontSize: 14 }} />,
  heart: <FavoriteBorderIcon sx={{ fontSize: 14 }} />,
  star: <StarBorderIcon sx={{ fontSize: 14 }} />,
  rect: <RectangleOutlinedIcon sx={{ fontSize: 14 }} />,
  circle: <CircleOutlinedIcon sx={{ fontSize: 14 }} />,
  oval: <LensIcon sx={{ fontSize: 12 }} />,
};

/**
 * Confetti body (Pro) for the Landing → Branding tab: a master on/off, a particle
 * Shape pill grid, and a Color Scheme list (each row has a Preview button that fires
 * a sample burst). No scheme selected ⇒ the default GfG brand palette. Ported from
 * the prototype's confetti config; renders live via the shared Confetti component.
 */
export default function ConfettiControls({ settings, onChange, onPreview }: ConfettiControlsProps) {
  const { confettiEnabled, confettiShape, confettiScheme } = settings;

  return (
    <div>
      {/* Master on/off */}
      <div className="flex items-center justify-between pb-3.5 mb-3.5 border-b border-light-dust">
        <span className="font-lato text-[13px] font-semibold text-rodeo-black">Show Confetti</span>
        <PillToggle
          checked={confettiEnabled}
          onChange={(v) => onChange({ confettiEnabled: v })}
          aria-label="Show confetti"
        />
      </div>

      <div className={confettiEnabled ? "" : "pointer-events-none opacity-40"}>
        {/* Shape */}
        <div className="mb-4">
          <span className="font-lato text-[11px] font-bold uppercase tracking-[0.5px] text-graphite block mb-2">
            Shape
          </span>
          <div className="flex flex-wrap gap-1.5">
            {CONFETTI_SHAPES.map((s) => {
              const active = confettiShape === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  // Select the shape AND fire a sample with it (+ the current scheme) so the
                  // change is immediately visible — otherwise a shape only shows on the next fire.
                  onClick={() => {
                    onChange({ confettiShape: s.id });
                    onPreview(confettiScheme);
                  }}
                  className={[
                    "inline-flex items-center gap-1 rounded-full border px-2.5 py-1.25 cursor-pointer transition-all",
                    "font-lato text-[12px]",
                    active
                      ? "border-dark-dust bg-clear-dust text-dark-dust"
                      : "border-smoke bg-white text-graphite hover:border-dark-dust hover:bg-clear-dust",
                  ].join(" ")}
                >
                  {SHAPE_ICONS[s.id]}
                  {s.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* Color Scheme */}
        <span className="font-lato text-[11px] font-bold uppercase tracking-[0.5px] text-graphite block mb-1">
          Color Scheme
        </span>
        <div className="flex flex-col">
          {CONFETTI_SCHEMES.map((scheme, i) => {
            const selected = confettiScheme === i;
            return (
              <div
                key={scheme.name}
                className={[
                  "flex items-center gap-2.5 rounded border-b border-card-border last:border-b-0 pr-1",
                  selected ? "bg-clear-dust" : "hover:bg-light-dust",
                ].join(" ")}
              >
                <button
                  type="button"
                  // Toggle: re-clicking the selected scheme clears it → default brand palette.
                  onClick={() => onChange({ confettiScheme: selected ? null : i })}
                  className="flex flex-1 items-center gap-2.5 py-2.25 pl-1.5 cursor-pointer text-left"
                  aria-pressed={selected}
                >
                  <span
                    className={[
                      "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
                      selected ? "border-dark-dust" : "border-smoke",
                    ].join(" ")}
                  >
                    {selected && <span className="h-2 w-2 rounded-full bg-dark-dust" />}
                  </span>
                  <span className="flex shrink-0 gap-0.75">
                    {scheme.colors.map((c) => (
                      <span
                        key={c}
                        className="h-2.5 w-2.5 rounded-full"
                        style={{ background: c, border: "1px solid rgba(0,0,0,0.08)" }}
                      />
                    ))}
                  </span>
                  <span className="flex-1 font-lato text-[13px] text-rodeo-black">
                    {scheme.name}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => onPreview(i)}
                  title="Preview"
                  aria-label={`Preview ${scheme.name} confetti`}
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-smoke bg-white text-graphite hover:border-dark-dust hover:bg-clear-dust hover:text-rodeo-black transition-all cursor-pointer"
                >
                  <CelebrationOutlinedIcon sx={{ fontSize: 16 }} />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
